import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, ChevronRight, ArrowRight, ArrowLeft, 
  TrendingUp, Check, Code2, Target 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Case Study Schema
const CaseStudySchema = ({ study }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": study.title,
    "description": study.problem,
    "publisher": {
      "@type": "Organization",
      "name": "Plutops"
    },
    "datePublished": study.published_at,
    "about": {
      "@type": "Thing",
      "name": study.client_name
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(`${API}/case-studies/${slug}`);
        setStudy(response.data);
        document.title = `${response.data.title} | Plutops Case Study`;
      } catch (error) {
        console.error('Failed to fetch case study:', error);
        setError('Case study not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-heading font-bold text-text-primary mb-4">Case Study Not Found</h1>
        <Link to="/case-studies">
          <Button className="bg-primary text-white rounded-full">
            View All Case Studies
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="case-study-detail-page">
      {/* Schema */}
      <CaseStudySchema study={study} />

      {/* Breadcrumb */}
      <div className="bg-warm-50 py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm text-text-muted">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <Link to="/case-studies" className="hover:text-primary">Case Studies</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-text-primary line-clamp-1">{study.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link 
                to="/case-studies" 
                className="inline-flex items-center text-primary hover:text-primary-hover mb-6 font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                All Case Studies
              </Link>

              <div className="flex items-center space-x-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <Building2 className="h-4 w-4 mr-1" />
                  {study.industry}
                </span>
              </div>

              <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-text-primary mb-4">
                {study.title}
              </h1>

              <p className="text-lg text-text-muted mb-8">
                Client: {study.client_name}
              </p>

              <div className="flex flex-wrap gap-2">
                {study.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 rounded-full bg-slate-100 text-text-secondary text-sm"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>

            {study.image_url && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <img
                  src={study.image_url}
                  alt={study.title}
                  className="w-full rounded-2xl shadow-xl"
                />
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mr-4">
                  <Target className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-text-primary">The Challenge</h2>
              </div>
              <p className="text-text-secondary leading-relaxed">
                {study.problem}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center mb-6">
                <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center mr-4">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-text-primary">Our Solution</h2>
              </div>
              <p className="text-text-secondary leading-relaxed">
                {study.solution}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block text-primary font-medium mb-4">Impact</span>
            <h2 className="font-heading font-bold text-3xl text-text-primary">
              Results & Metrics
            </h2>
          </motion.div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary/5">
                  <TableHead className="font-heading font-bold text-text-primary">Metric</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Before</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">After</TableHead>
                  <TableHead className="font-heading font-bold text-primary">Improvement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {study.results.map((result, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-slate-100"
                  >
                    <TableCell className="font-medium">{result.metric}</TableCell>
                    <TableCell className="text-text-secondary">{result.before}</TableCell>
                    <TableCell className="text-text-secondary">{result.after}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center text-primary font-medium">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        {result.improvement}
                      </span>
                    </TableCell>
                  </motion.tr>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* Architecture */}
      {study.architecture_description && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-8">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4">
                  <Code2 className="h-6 w-6 text-primary" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-text-primary">
                  Technical Architecture
                </h2>
              </div>
              
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                <p className="text-text-secondary leading-relaxed">
                  {study.architecture_description}
                </p>
                
                <div className="mt-8 flex flex-wrap gap-3">
                  {study.technologies.map((tech, i) => (
                    <span
                      key={i}
                      className="px-4 py-2 rounded-lg bg-white text-text-primary text-sm font-medium border border-slate-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl text-white mb-6">
              Ready to Achieve Similar Results?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Let's discuss how we can help transform your business with our proven approach.
            </p>
            <Link to="/contact">
              <Button
                data-testid="case-study-cta-btn"
                className="bg-white text-primary hover:bg-warm-100 rounded-full px-8 py-6 text-lg"
              >
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetailPage;
